import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Church, Loader2, UserPlus, AlertTriangle, UserCircle } from "lucide-react";
import { toast } from "sonner";

export default function CheckinPage() {
  const [params] = useSearchParams();
  const eventId = params.get("event_id");

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [searchResults, setSearchResults] = useState<{ id: string; full_name: string }[]>([]);
  const [selectedMember, setSelectedMember] = useState<{ id: string; full_name: string } | null>(null);
  const [isVisitor, setIsVisitor] = useState(false);
  const [checkedIn, setCheckedIn] = useState(false);

  const { data: event, isLoading: eventLoading } = useQuery({
    queryKey: ["checkin_event", eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, name, event_type, event_date, start_time")
        .eq("id", eventId!)
        .eq("is_active", true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!eventId,
  });

  // Search members using RPC (church-scoped via event)
  useEffect(() => {
    if (isVisitor || name.length < 2 || !eventId) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      const { data } = await supabase.rpc("search_members_by_event" as any, {
        _event_id: eventId,
        _search: name,
      });
      const results = (data as any[]) || [];
      if (results.length > 0) {
        setSearchResults(results.map((r: any) => ({
          id: r.id,
          full_name: r.full_name,
        })));
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [name, isVisitor, eventId]);

  const checkinMutation = useMutation({
    mutationFn: async () => {
      const payload: any = {
        event_id: eventId,
        member_name: selectedMember?.full_name || name.trim(),
        phone: phone.trim() || null,
        email: isVisitor ? email.trim() || null : null,
        is_visitor: isVisitor,
        member_id: selectedMember?.id || null,
        status: "present",
      };
      const { error } = await supabase.from("attendance_records").insert(payload);
      if (error) {
        if (error.code === "23505") throw new Error("You are already checked in for this event.");
        throw error;
      }
    },
    onSuccess: () => setCheckedIn(true),
    onError: (err: Error) => toast.error(err.message),
  });

  const handleCheckin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() && !selectedMember) { toast.error("Please enter your name"); return; }
    if (isVisitor && email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }
    checkinMutation.mutate();
  };

  const selectMember = (m: { id: string; full_name: string }) => {
    setSelectedMember(m); setName(m.full_name); setSearchResults([]);
  };

  if (!eventId) {
    return (
      <div className="min-h-screen brand-page-bg flex items-center justify-center p-4">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-destructive mb-4" />
          <h1 className="text-xl font-bold text-foreground">Invalid Check-in Link</h1>
          <p className="text-muted-foreground mt-2">No event specified. Please scan a valid QR code.</p>
        </div>
      </div>
    );
  }

  if (eventLoading) {
    return <div className="min-h-screen brand-page-bg flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  if (!event) {
    return (
      <div className="min-h-screen brand-page-bg flex items-center justify-center p-4">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-destructive mb-4" />
          <h1 className="text-xl font-bold text-foreground">Event Not Found</h1>
          <p className="text-muted-foreground mt-2">This event may have ended or the link is invalid.</p>
        </div>
      </div>
    );
  }

  if (checkedIn) {
    return (
      <div className="min-h-screen brand-page-bg flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-success" />
          </div>
          <h1 className="text-2xl font-heading font-bold text-foreground">You're Checked In!</h1>
          <p className="text-muted-foreground mt-2">Welcome, <span className="font-medium text-foreground">{selectedMember?.full_name || name}</span></p>
          <div className="mt-4 p-3 bg-secondary/50 rounded-lg">
            <p className="text-sm font-medium text-foreground">{event.name}</p>
            <p className="text-xs text-muted-foreground">{event.event_date} · {event.event_type}</p>
          </div>
          {isVisitor && <p className="text-xs text-muted-foreground mt-4">Thank you for visiting!</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen brand-page-bg flex items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Church className="w-7 h-7 text-primary" />
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground">{event.name}</h1>
          <div className="flex items-center justify-center gap-2 mt-2">
            <Badge variant="secondary">{event.event_type}</Badge>
            <span className="text-sm text-muted-foreground">{event.event_date}</span>
          </div>
        </div>

        <div className="flex rounded-lg border border-border overflow-hidden">
          <button type="button" className={`flex-1 py-2.5 text-sm font-medium transition-colors ${!isVisitor ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-secondary/50"}`}
            onClick={() => { setIsVisitor(false); setSelectedMember(null); setName(""); setPhone(""); setEmail(""); }}>Member</button>
          <button type="button" className={`flex-1 py-2.5 text-sm font-medium transition-colors ${isVisitor ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-secondary/50"}`}
            onClick={() => { setIsVisitor(true); setSelectedMember(null); setName(""); setPhone(""); setEmail(""); setSearchResults([]); }}>
            <UserPlus className="w-4 h-4 inline mr-1" /> Visitor
          </button>
        </div>

        <form onSubmit={handleCheckin} className="space-y-4">
          <div className="space-y-2 relative">
            <Label>{isVisitor ? "Your Name" : "Search Your Name"}</Label>
            <Input value={name} onChange={(e) => { setName(e.target.value); setSelectedMember(null); }} placeholder={isVisitor ? "Enter your name" : "Start typing your name..."} autoComplete="off" autoFocus />
            {searchResults.length > 0 && !selectedMember && (
              <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {searchResults.map((m) => (
                  <button key={m.id} type="button" className="w-full text-left px-4 py-3 text-sm hover:bg-accent/10 transition-colors border-b border-border/50 last:border-0 flex items-center gap-2" onClick={() => selectMember(m)}>
                    <UserCircle className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground">{m.full_name}</p>
                      <p className="text-xs text-muted-foreground">
                        <span className="font-mono">M-{m.id.slice(0, 6).toUpperCase()}</span>
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {selectedMember && <p className="text-xs text-success font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Found: {selectedMember.full_name}</p>}
          </div>
          {isVisitor && (
            <>
              <div className="space-y-2"><Label>Phone</Label><Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Your phone number" /></div>
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" /></div>
            </>
          )}
          <Button type="submit" className="w-full h-12 text-base" disabled={checkinMutation.isPending || (!name.trim() && !selectedMember)}>
            {checkinMutation.isPending ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Checking in...</> : <><CheckCircle2 className="w-5 h-5 mr-2" /> Check In</>}
          </Button>
        </form>
      </div>
    </div>
  );
}
